import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';

/**
 * Read-only product preview; the linked product page owns variant and cart behavior.
 * @typedef {object} QuickViewRefs
 * @property {HTMLDialogElement} dialog
 * @extends Component<QuickViewRefs>
 */
class LategridQuickView extends Component {
  requiredRefs = ['dialog'];
  #controller;
  #opener;

  connectedCallback() {
    super.connectedCallback();
    this.#connectDialog();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#connectDialog();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    if (this.refs.dialog?.open) this.refs.dialog.close();
    if (this.refs.dialog) unlockScroll(this.refs.dialog);
    super.disconnectedCallback();
  }

  #connectDialog() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.refs.dialog.addEventListener('close', () => {
      unlockScroll(this.refs.dialog);
      if (this.#opener instanceof HTMLElement && this.#opener.isConnected) this.#opener.focus();
    }, { signal: this.#controller.signal });
  }

  /** Opens a native modal, which contains keyboard focus and supports Escape. */
  showDialog() {
    if (this.refs.dialog.open) return;
    this.#opener = document.activeElement;
    this.refs.dialog.showModal();
    lockScroll(this.refs.dialog);
  }

  /** Closes the native dialog and restores focus through its close listener. */
  closeDialog() {
    this.refs.dialog.close();
  }

  /** @param {MouseEvent} event */
  backdropClick(event) {
    if (event.target !== this.refs.dialog) return;
    const rect = this.refs.dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
      this.closeDialog();
    }
  }
}

/**
 * Small-catalog filtering with stable featured order and data-driven price sorting.
 * @typedef {object} CatalogRefs
 * @property {HTMLElement} grid
 * @property {HTMLElement} status
 * @property {HTMLElement} empty
 * @property {HTMLButtonElement} clear
 * @property {HTMLSelectElement} sort
 * @extends Component<CatalogRefs>
 */
class LategridCatalog extends Component {
  requiredRefs = ['grid', 'status', 'empty', 'clear', 'sort'];
  #controller;
  #cards = [];
  #state = { beam: '', color: '', sort: 'featured' };

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    super.disconnectedCallback();
  }

  #setup() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#cards = Array.from(this.refs.grid.children).filter((node) => node instanceof HTMLElement && node.hasAttribute('data-catalog-card'));
    this.#readUrl();
    window.addEventListener('popstate', () => this.#readUrl(), { signal: this.#controller.signal });
  }

  #readUrl() {
    const params = new URL(window.location.href).searchParams;
    const beam = params.get('beam') || '';
    const color = params.get('color') || '';
    const sort = params.get('sort') || 'featured';
    this.#state = {
      beam: ['spot', 'flood', 'combo'].includes(beam) ? beam : '',
      color: ['white', 'amber'].includes(color) ? color : '',
      sort: ['price-ascending', 'price-descending'].includes(sort) ? sort : 'featured',
    };
    this.#render();
  }

  /** @param {MouseEvent} event */
  filter(event) {
    if (!(event.target instanceof HTMLButtonElement)) return;
    const { filterKind: kind, filterValue: value } = event.target.dataset;
    if ((kind !== 'beam' && kind !== 'color') || !value) return;
    this.#state[kind] = this.#state[kind] === value ? '' : value;
    this.#render();
    this.#writeUrl();
  }

  /** @param {Event} event */
  sort(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;
    this.#state.sort = event.target.value;
    this.#render();
    this.#writeUrl();
  }

  clearFilters() {
    this.#state.beam = '';
    this.#state.color = '';
    this.#render();
    this.#writeUrl();
    const firstChip = this.querySelector('[data-filter-kind]');
    if (firstChip instanceof HTMLButtonElement) firstChip.focus();
  }

  #writeUrl() {
    const url = new URL(window.location.href);
    for (const [key, value] of Object.entries(this.#state)) {
      if (!value || value === 'featured') url.searchParams.delete(key);
      else url.searchParams.set(key, value);
    }
    window.history.replaceState(window.history.state, '', url);
  }

  #render() {
    const { beam, color, sort } = this.#state;
    const ordered = [...this.#cards].sort((a, b) => {
      if (sort === 'price-ascending') return Number(a.dataset.price) - Number(b.dataset.price);
      if (sort === 'price-descending') return Number(b.dataset.price) - Number(a.dataset.price);
      return Number(a.dataset.position) - Number(b.dataset.position);
    });
    let count = 0;
    for (const card of ordered) {
      const colors = (card.dataset.colors || '').split(',').map((value) => value.trim());
      const visible = (!beam || card.dataset.beam === beam) && (!color || colors.includes(color));
      card.hidden = !visible;
      if (visible) count += 1;
      this.refs.grid.append(card);
    }
    for (const button of this.querySelectorAll('[data-filter-kind]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      const kind = button.dataset.filterKind;
      if (kind === 'beam' || kind === 'color') button.setAttribute('aria-pressed', String(this.#state[kind] === button.dataset.filterValue));
    }
    this.refs.sort.value = sort;
    this.refs.clear.hidden = !beam && !color;
    this.refs.empty.hidden = count > 0;
    this.refs.status.textContent = count === 1 ? this.dataset.oneProduct : this.dataset.manyProducts?.replace('[count]', String(count));
  }
}

if (!customElements.get('lategrid-quick-view-component')) customElements.define('lategrid-quick-view-component', LategridQuickView);
if (!customElements.get('lategrid-catalog-component')) customElements.define('lategrid-catalog-component', LategridCatalog);
